<template>
  <div>
    <!-- ================= TOP BAR ================= -->
    <div class="card mb-25 p-3 border-0 rounded-0 bg-white letter-spacing">
      <div class="card-body p-15 p-sm-20 p-md-25">
        <div class="row align-items-center mb-4">
          <div class="col-md-6 d-flex align-items-center gap-2">
            <label>Voir</label>
            <v-select
              class="per-page-selector"
              :options="[10, 25]"
              :clearable="false"
              v-model="users.per_page"
              @update:modelValue="getRecords(1)"
            />
            <label>sur {{ users.total }} entité</label>
          </div>

          <div class="col-md-6 text-end">
            <input
              v-model="users.searchTerm"
              @change="getRecords(1)"
              class="form-control d-inline-block w-auto"
              placeholder="Chercher..."
            />
          </div>
        </div>

        <!-- ================= TABLE ================= -->

        <div v-if="isLoader" class="text-center">
          <div class="spinner-border text-primary"></div>
        </div>

        <vue-good-table
          :columns="columns"
          :rows="rows"
          responsive
        >
          <template #emptystate>
            <div class="text-center text-muted">
              Aucune entrées correspondants trouvés
            </div>
          </template>

          <template #table-row="{ row, column, formattedRow }">
            <span v-if="column.field === 'code'">
              <h6>{{ row.code }}</h6>
            </span>

            <span v-else-if="column.field === 'note'">
              <div style="word-wrap: anywhere">
                {{ row.note }}
              </div>
            </span>

            <span v-else-if="column.field === 'source'">
              <img
                :src="row.source === 'sheet' ? sheetImg : wooImg"
                style="width: 65px"
              />
            </span>

            <span v-else-if="column.field === 'created_at'">
              <h5>{{ getDate(row.created_at) }}</h5>
            </span>

            <span v-else-if="column.field === 'receiver'">
              <b>{{ row.receiver }}</b>
            </span>

            <span v-else-if="column.field === 'city'">
              {{ row.city }}
            </span>

            <span v-else-if="column.field === 'price'">
              <b>{{ row.price }}</b>
            </span>

            <span v-else-if="column.field === 'phone'">
              <b>{{ row.phone }}</b>
            </span>

            <span
              v-else-if="column.field === 'error'"
              v-html="row.error"
            ></span>

            <!-- ACTIONS -->
            <span v-else-if="column.field === 'action'">
              <button
                class="btn btn-secondary btn-sm me-1"
                @click="openEdit(row)"
              >
                ✏
              </button>

              <button
                v-if="row.solved === 0"
                class="btn btn-danger btn-sm"
                @click="DestroySheetColi(row)"
              >
                🗑
              </button>
            </span>

            <span v-else>
              {{ formattedRow[column.field] }}
            </span>
          </template>
        </vue-good-table>

        <div class="row mt-3">
          <div class="col-md-6"></div>
          <div class="col-md-6">
            <vue-pagination
              :pagination="users"
              @paginate="getRecords"
              :offset="9"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- ================= MODAL ================= -->
    <div class="modal fade" id="editModal" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Modifier Colis</h5>
            <button class="btn-close" data-bs-dismiss="modal"></button>
          </div>

          <div class="modal-body">
            <form @submit.prevent="EditColi">
              <div class="row g-2">
                <div class="col-md-6">
                  <label class="form-label">Code suivi</label>
                  <input v-model="ColiDetails.code" class="form-control" />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Destinataire</label>
                  <input v-model="ColiDetails.dest" class="form-control" />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Téléphone</label>
                  <input v-model="ColiDetails.phone" class="form-control" />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Prix</label>
                  <input
                    type="number"
                    v-model="ColiDetails.price"
                    class="form-control"
                  />
                </div>

                <div class="col-12">
                  <label class="form-label">Ville</label>
                  <v-select
                    label="name"
                    :options="CitiesList"
                    v-model="ColiDetails.city"
                  />
                </div>

                <div class="col-md-6">
                  <label class="form-label">Adresse</label>
                  <textarea
                    v-model="ColiDetails.adresse"
                    class="form-control"
                  ></textarea>
                </div>

                <div class="col-md-6">
                  <label class="form-label">Commentaire</label>
                  <textarea
                    v-model="ColiDetails.note"
                    class="form-control"
                  ></textarea>
                </div>
              </div>

              <div class="text-end mt-3">
                <button class="btn btn-danger me-2" data-bs-dismiss="modal">
                  Annuler
                </button>
                <button class="btn btn-warning" type="submit">
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import moment from "moment";

import sheetImg from "@/assets/images/sheet.png";
import wooImg from "@/assets/images/woo.png";
import api from "@/utils/axios";
import { Modal } from "bootstrap";

const rows = ref([]);
const isLoader = ref(false);
const CitiesList = ref([]);
const ColiDetails = ref({});

const users = reactive({
  searchTerm: "",
  total: 0,
  per_page: 10,
  current_page: 1,
});

const columns = [
  { label: "Source", field: "source" },
  { label: "Erreur", field: "error" },
  { label: "Code d'envoi", field: "code" },
  { label: "Destinataire", field: "dest" },
  { label: "Commentaire", field: "note" },
  { label: "Téléphone", field: "phone" },
  { label: "Adresse", field: "adresse" },
  { label: "Prix", field: "price" },
  { label: "Date", field: "created_at" },
  { label: "Action", field: "action" },
];

const getDate = (v) => moment(v).format("YYYY-MM-DD HH:mm");

const getRecords = async (page = 1) => {
  isLoader.value = true;
  users.current_page = page;
  const res = await api.get("/api/auth/client/colis_erroned", {
    params: {
      searchTerm: users.searchTerm,
      page,
      per_page: users.per_page,
    },
  });
  rows.value = res.data.users.data;
  Object.assign(users, res.data.users);
  isLoader.value = false;
};

const openEdit = (row) => {
  ColiDetails.value = { ...row };
  new Modal("#editModal").show();
};

const DestroySheetColi = (row) => {
  if (!confirm("Supprimer ce colis ?")) return;
  api
    .post(`/api/auth/client/deleteSheetColis/${row.id}`, {
      sheet_id: row.sheet_id,
    })
    .then(getRecords);
};

const EditColi = () => {
  api
    .post(
      `/api/auth/client/editErrorSheet/${ColiDetails.value.id}`,
      ColiDetails.value
    )
    .then(() => {
      new Modal("#editModal").hide();
      getRecords();
    });
};

onMounted(() => {
  api.get("/api/auth/citiesList").then((r) => (CitiesList.value = r.data));
  getRecords();
});
</script>

<style scoped>
.per-page-selector {
  width: 90px;
}
</style>
